import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import * as EventUtils from '../utilities/events';
import * as FilterUtils from '../utilities/filter';
import * as InitUtils from '../utilities/init';
import Button from './Button';
import Employees from './Employees';
import Filter from './Filter';
import Header from './Header';
import JobTileGroup from './JobTileGroup';
import Loader from './Loader';

export default function App({ filteredScope, restUrl, showFilter, showFaces }) {
	const [appData, setAppData] = useState(null);
	const [userData, setUserData] = useState(null);
	const [currentlyVisibleJobs, setCurrentlyVisibleJobs] = useState(null);

	// Fetch data from REST API
	useEffect(() => InitUtils.initializeApp({ restUrl, showFilter, setAppData, setUserData }), []);

	useEffect(() => {
		if (!(appData && userData)) {
			return;
		}

		if (!FilterUtils.filterIsSet(userData) && appData.jobs.length > appData.general.job_count) {
			setCurrentlyVisibleJobs(appData.general.job_count);
		}
	}, [userData]);

	// Update URL search params depending on current filter configuration
	useEffect(() => {
		if (!(appData && userData)) {
			return;
		}

		const url = new URL(window.location);
		const filterKeys = Object.keys(appData.taxonomies);
		filterKeys.push('q'); // Add search param

		filterKeys.forEach((filterKey) => {
			if (filterKey in userData.taxonomies) {
				// Set the filter value in the URL
				url.searchParams.set(filterKey, userData.taxonomies[filterKey]);
			} else if (url.searchParams.has(filterKey)) {
				// Remove the filter value from the URL
				url.searchParams.delete(filterKey);
			}
		});

		history.replaceState(null, '', url.toString());
	}, [appData, userData]);

	// EventUtils
	const handleLoadMore = () => EventUtils.loadMoreBtnHandler(appData, currentlyVisibleJobs, setCurrentlyVisibleJobs);
	const handleSearch = (e) => EventUtils.handleSearchInput(e, userData, setUserData);
	const handleFilterChange = (taxonomyName, value, isDisabled) => EventUtils.handleFilterChange(taxonomyName, value, isDisabled, userData, setUserData);
	const resetFilters = () => EventUtils.resetAllFilters(userData, setUserData);
	const handleFilterClear = (filterName) => EventUtils.handleFilterClear(filterName, userData, setUserData);

	// FilterUtils
	const getFilteredJobs = () => FilterUtils.getFilteredJobs(appData, userData, currentlyVisibleJobs);
	const isOptionDisabled = (taxonomyName, option) => FilterUtils.isOptionDisabled(taxonomyName, option, appData, userData);

	const getLabels = () => appData?.labels || {};
	const getLabel = (key) => getLabels()[key] || '';

	return appData ? (
		<>
			<Header
				getLabel={getLabel}
				jobsCount={FilterUtils.getFilteredJobs(appData, userData, null).length}
				filteredTaxonomies={userData.taxonomies}
				taxonomies={appData.taxonomies}
				pictures={appData.graphics}
			/>
			{showFilter && (
				<Filter
					taxonomies={appData.taxonomies}
					filteredTaxonomies={userData.taxonomies}
					handleSearchInput={(e) => handleSearch(e)}
					handleFilterChange={handleFilterChange}
					handleFilterClear={handleFilterClear}
					isOptionDisabled={isOptionDisabled}
					getLabel={getLabel}
					filteredScope={filteredScope}
				/>
			)}
			<div className="jobs__content">
				{showFaces && <Employees jobCount={getFilteredJobs().length} pictures={appData.graphics} />}
				<JobTileGroup resetAllFilters={resetFilters} getLabel={getLabel} getFilteredJobs={getFilteredJobs} taxonomies={appData.taxonomies} />
			</div>
			{!FilterUtils.filterIsSet(userData) && currentlyVisibleJobs && <Button label={getLabel('load_more')} onClick={handleLoadMore} center />}
		</>
	) : (
		<Loader />
	);
}

App.propTypes = {
	filteredScope: PropTypes.bool,
	restUrl: PropTypes.string,
	showFilter: PropTypes.bool,
	showFaces: PropTypes.bool,
};
